import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ChartData,
  ChartJsType,
  ChartOptions,
} from '../../models/chart.models';
import { ChartDataset } from 'chart.js';
import { ChartBase } from '../base/base.chart';
import 'chartjs-adapter-luxon';
import { CHANGE_VISIBILITY_TOKEN } from '../line/line.chart';

@Component({
  selector: 'chart-pie',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartBase],
  template: `
    <div class="flex flex-col gap-y-4 w-full">
      <chart-base
        [type]="type()"
        [data]="data()"
        [options]="optionsComputed()"
      />
    </div>
  `,
})
export class ChartPieComponent {
  private changeVisibilityFn = inject(CHANGE_VISIBILITY_TOKEN, {
    optional: true,
  });

  onChangeVisibility(event: any) {
    this.changeVisibilityFn?.(event);
  }
  /**
   * `labels`: Required input defining the data labels of the pie chart.
   */
  labels = input<string[]>([]);

  /**
   * `datasets`: Required input defining the data values of the pie chart.
   */
  datasets = input<ChartDataset<'pie'>[]>([]);

  /**
   * `options`: Optional input to provide additional configuration options.
   */
  options = input<ChartOptions<'pie'>>();

  /**
   * Computed `optionsComputed` property configuring responsiveness,
   * grid display, and scaling for the y-axis. Supports optional annotations.
   */
  optionsComputed = computed<ChartOptions<'pie'>>(() => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 16,
            boxHeight: 16,
            pointStyle: 'circle',
            usePointStyle: true,
            textAlign: 'center',
          },
        },
        tooltip: { enabled: true },
      },
    };
  });

  /**
   * Computed `data` property defining the structure of the bar chart.
   * Labels are empty since this is a generic bar chart.
   */
  data = computed<ChartData<'pie'>>(() => {
    return { labels: this.labels(), datasets: this.datasets() };
  });

  /**
   * `type`: Specifies the chart type as `pie`.
   */
  type = signal<ChartJsType>('pie');
}
